/**
 * Environment Configuration
 * Centralized access to environment variables
 */

export interface AppConfig {
    apiBaseUrl: string;
    appName: string;
    debugMode: boolean;
    logLevel: 'debug' | 'info' | 'warn' | 'error';
    stripePublishableKey: string;
    /** Stripe Price id for the Pro plan, used by the upgrade flow. */
    googleClientId: string;
    microsoftClientId: string;
    appleClientId: string;
}

function getEnvVar(key: string, fallback: string = ''): string {
    return import.meta.env[key] || fallback
}

function getBooleanEnvVar(key: string, fallback: boolean = false): boolean {
    const value = getEnvVar(key)
    if (value === '') return fallback
    return value.toLowerCase() === 'true'
}

export const config: AppConfig = {
    apiBaseUrl: getEnvVar('VITE_API_BASE_URL',
        import.meta.env.DEV ? 'http://dev.localhost:8300/api' : ''),
    appName: getEnvVar('VITE_APP_NAME', 'DocMorphia'),
    debugMode: getBooleanEnvVar('VITE_DEBUG_MODE', import.meta.env.DEV),
    logLevel: (getEnvVar('VITE_LOG_LEVEL', import.meta.env.DEV ? 'debug' : 'info') as AppConfig['logLevel']),
    stripePublishableKey: getEnvVar('VITE_STRIPE_PUBLISHABLE_KEY', ''),
    // Read here rather than in BillingPage, which reached for import.meta.env directly and so sat
    // outside the one place every other frontend setting is resolved.
    googleClientId: getEnvVar('VITE_GOOGLE_CLIENT_ID', ''),
    microsoftClientId: getEnvVar('VITE_MICROSOFT_CLIENT_ID', ''),
    appleClientId: getEnvVar('VITE_APPLE_CLIENT_ID', ''),
}

export const isDevelopment = import.meta.env.DEV
export const isProduction = import.meta.env.PROD

function getRuntimeEnvironment(): string {
    const explicitEnv = getEnvVar('VITE_RUNTIME_ENVIRONMENT')
    if (explicitEnv) return explicitEnv

    const apiUrl = config.apiBaseUrl
    if (apiUrl.includes('dev.localhost') || apiUrl.includes('localhost')) return 'dev-local'
    if (apiUrl.includes('dev-api.docmorphia.com')) return 'dev'
    if (apiUrl.includes('staging-api.docmorphia.com')) return 'staging'
    if (apiUrl.includes('api.docmorphia.com')) return 'prod'
    return import.meta.env.MODE
}

export const runtimeEnvironment = getRuntimeEnvironment()

export function getHealthVersionUrl(): string {
    const base = config.apiBaseUrl
    return base.endsWith('/api') ? `${base}/v1/health/version` : `${base}/api/v1/health/version`
}
